import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { USE_MOCK } from "@/api/client";
import { integrationsApi } from "@/api/endpoints/integrations";
import { d365BusinessCentralApi } from "@/api/endpoints/d365BusinessCentral";
import { composioApi } from "@/api/endpoints/composio";
import { notificationsApi } from "@/api/endpoints/notifications";
import { parseToolkit } from "@/shared/lib/toolkit";
import {
  INTEGRATION_DISCONNECTED_TYPE,
  composioVerdicts,
  d365BcVerdict,
  disconnectDedupKey,
  emailVerdict,
  zohoVerdicts,
  type IntegrationVerdict,
} from "../lib/integration-health";
import { NOTIFICATION_KEYS } from "./use-notifications";

const CHECK_INTERVAL_MS = 15 * 60_000;
// Let the app's own first-paint requests go first.
const FIRST_CHECK_DELAY_MS = 20_000;

/** Never throws: a failed status fetch is `null` → verdict `unknown` → no action. */
async function orNull<T>(p: Promise<T>): Promise<T | null> {
  try {
    return await p;
  } catch {
    return null;
  }
}

async function collectVerdicts(): Promise<IntegrationVerdict[]> {
  const [email, zoho, d365bc, composio] = await Promise.all([
    orNull(integrationsApi.getEmailStatus().then((r) => r.data)),
    orNull(integrationsApi.listZohoIntegrations()),
    orNull(d365BusinessCentralApi.getIntegration()),
    orNull(composioApi.getStatus()),
  ]);
  return [
    emailVerdict(email),
    ...zohoVerdicts(zoho),
    d365BcVerdict(d365bc),
    ...composioVerdicts(composio, (slug) => parseToolkit(slug).displayName),
  ];
}

/**
 * Integration-disconnect watcher. Re-checks the same status endpoints the
 * Integrations page reads and, when a connected app has stopped working, raises a
 * "<App> disconnected — reconnect needed" notification (bell + sound via the SSE
 * stream) that links to /integrations.
 *
 * Why the frontend: the health signals live in different backends (Zoho, D365,
 * Composio), and the Integrations page already knows how to read each one.
 * Checking here guarantees the notification never disagrees with the page it
 * sends the user to. Email is the exception: email-backend raises it server-side
 * from the real Aurinko 401 (see `emailVerdict`); this hook only clears it.
 *
 * - Deduped per integration per UTC day (see `disconnectDedupKey`), so a
 *   still-broken app re-reminds at most daily.
 * - When an app is healthy again, its unread disconnect notifications are marked
 *   read so the bell doesn't keep nagging after a reconnect.
 * - Skips while the tab is hidden, and never uses refetch-on-focus (a focus
 *   request storm previously starved session revalidation → auto-logout).
 */
export function useIntegrationHealth(userId: string | null | undefined): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!userId || USE_MOCK) return;
    let cancelled = false;
    let running = false;

    const check = async () => {
      if (cancelled || running || document.visibilityState === "hidden") return;
      running = true;
      try {
        const verdicts = await collectVerdicts();
        if (cancelled) return;
        let changed = false;

        for (const v of verdicts) {
          if (v.state !== "broken") continue;
          const { created } = await notificationsApi
            .selfEmit({
              type: INTEGRATION_DISCONNECTED_TYPE,
              title: `${v.name} disconnected — please reconnect`,
              body: v.body,
              link: "/integrations",
              tone: "warn",
              dedupKey: disconnectDedupKey(v.key),
              meta: { integration: v.key, source: "integration-health" },
            })
            .catch(() => ({ created: false }));
          changed ||= created;
        }

        const healthy = new Set(verdicts.filter((v) => v.state === "ok").map((v) => v.key));
        if (healthy.size) {
          const unread = await orNull(notificationsApi.list({ unreadOnly: true, limit: 50 }));
          for (const n of unread?.items ?? []) {
            const key = n.meta?.integration;
            if (n.type === INTEGRATION_DISCONNECTED_TYPE && typeof key === "string" && healthy.has(key)) {
              await notificationsApi.markRead(n.id).catch(() => undefined);
              changed = true;
            }
          }
        }

        if (changed) void queryClient.invalidateQueries({ queryKey: NOTIFICATION_KEYS.all });
      } finally {
        running = false;
      }
    };

    const first = setTimeout(() => void check(), FIRST_CHECK_DELAY_MS);
    const timer = setInterval(() => void check(), CHECK_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearTimeout(first);
      clearInterval(timer);
    };
  }, [userId, queryClient]);
}
